import DOMPurify from "dompurify";
import Papa from "papaparse";
import { format as formatNumber } from "ssf";
import type { ArtifactContent } from "@/shared/types";

export type ArtifactKind = "document" | "presentation" | "sheet";
export type Cell = { value: string | number | boolean | null; formula?: string; format?: string };
export type Worksheet = { name: string; rows: Cell[][] };
export type ViewerArtifact = { kind: ArtifactKind; html: string; sheets: Worksheet[] };
export type ViewerFile = { name: string; file?: File; url?: string };
export const escapeHtml = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));

// Active content and remote assets never enter the app DOM or export canvas.
export function cleanHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["article", "section", "div", "span", "h1", "h2", "h3", "h4", "p", "br", "strong", "b", "em", "i", "u", "s", "ul", "ol", "li", "table", "thead", "tbody", "tr", "th", "td", "blockquote", "pre", "code", "img", "hr"],
    ALLOWED_ATTR: ["src", "alt", "data-slide", "data-sheet", "data-type", "data-value", "data-formula", "data-format"],
    ALLOW_DATA_ATTR: false,
  });
  const doc = new DOMParser().parseFromString(clean, "text/html");
  doc.querySelectorAll("img").forEach(img => {
    if (!/^data:image\/(png|jpeg|webp);base64,[a-z0-9+/=\s]+$/i.test(img.getAttribute("src") || "")) img.remove();
  });
  return doc.body.innerHTML;
}

export function parseSheets(html: string): Worksheet[] {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const tables = Array.from(doc.querySelectorAll("table[data-sheet]"));
  if (!tables.length) tables.push(...Array.from(doc.querySelectorAll("table")));
  const names = new Set<string>();
  return tables.map((table, index) => {
    const name = table.getAttribute("data-sheet") || `Sheet ${index + 1}`;
    if (name.length > 31 || /[\\/*?:\[\]]/.test(name) || names.has(name.toLowerCase())) throw new Error("Worksheet names must be unique, at most 31 characters, and contain no \\ / * ? : [ ].");
    names.add(name.toLowerCase());
    return { name, rows: Array.from(table.querySelectorAll("tr")).map(row => Array.from(row.children).filter(el => /^(TD|TH)$/.test(el.tagName)).map(el => {
      const raw = el.getAttribute("data-value") ?? el.textContent ?? "";
      const type = el.getAttribute("data-type");
      if (type === "number" && (!raw.trim() || !Number.isFinite(Number(raw)))) throw new Error(`Invalid number in ${name}.`);
      if (type === "boolean" && !["true", "false"].includes(raw)) throw new Error(`Invalid boolean in ${name}.`);
      const formula = el.getAttribute("data-formula")?.replace(/^=/, "");
      if (formula && (!el.hasAttribute("data-value") || /[\[\]|]|https?:|(?:WEBSERVICE|HYPERLINK|RTD|CALL|REGISTER\.ID|EXEC|RUN|EVALUATE|IMPORTXML|IMPORTDATA|IMAGE)\s*\(/i.test(formula))) throw new Error("Formulas require a cached data-value and cannot reference external resources.");
      return { value: type === "number" ? Number(raw) : type === "boolean" ? raw === "true" : raw, formula, format: el.getAttribute("data-format") || undefined };
    })) };
  });
}

export function toArtifact(content: ArtifactContent): ViewerArtifact {
  let kind: ArtifactKind = "document";
  let html = "";
  if (content.kind === "doc") html = `<h1>${escapeHtml(content.heading)}</h1>${content.subtitle ? `<p>${escapeHtml(content.subtitle)}</p>` : ""}${content.sections.map(s => `${s.h ? `<h2>${escapeHtml(s.h)}</h2>` : ""}<p>${escapeHtml(s.p)}</p>`).join("")}`;
  if (content.kind === "slides") {
    kind = "presentation";
    html = content.slides.map(s => `<section data-slide><h1>${escapeHtml(s.title)}</h1><ul>${s.bullets.map(b => `<li>${escapeHtml(b)}</li>`).join("")}</ul></section>`).join("");
  }
  if (content.kind === "sheet") {
    kind = "sheet";
    html = `<table data-sheet="Sheet 1"><tr>${content.columns.map(c => `<th>${escapeHtml(c)}</th>`).join("")}</tr>${content.rows.map(r => `<tr>${r.map(c => `<td>${escapeHtml(c)}</td>`).join("")}</tr>`).join("")}</table>`;
  }
  if (content.kind === "html") {
    kind = content.artifactType || "document";
    html = content.html;
  }
  html = cleanHtml(html);
  if (!html.trim()) throw new Error("This artifact has no viewable content.");
  if (kind === "presentation" && !new DOMParser().parseFromString(html, "text/html").querySelector("[data-slide]")) throw new Error("Presentations must include a section with data-slide for each slide.");
  const sheets = kind === "sheet" ? parseSheets(html) : [];
  if (kind === "sheet" && !sheets.length) throw new Error("Sheets must include a table with data-sheet.");
  return { kind, html, sheets };
}

export function parseCsv(text: string, delimiter = ","): Worksheet {
  const result = Papa.parse<string[]>(text.replace(/^\uFEFF/, ""), { delimiter, skipEmptyLines: true });
  if (result.errors.length) throw new Error(`Unable to read CSV: ${result.errors[0].message}`);
  return { name: "Sheet 1", rows: result.data.map(row => row.map(value => ({ value }))) };
}
export function displayCell(cell?: Cell): string {
  if (!cell || cell.value == null) return "";
  if (cell.format && typeof cell.value === "number") {
    try { return formatNumber(cell.format, cell.value); }
    catch { return String(cell.value); /* Unknown number format: show the stored value. */ }
  }
  return String(cell.value);
}
export function sheetCsv(sheet: Worksheet): string {
  // Strings stay strings when opened by Excel; formula results export as values.
  return Papa.unparse(sheet.rows.map(row => row.map(c => c.value ?? "")), { escapeFormulae: true, newline: "\r\n" });
}
export function filename(title: string, extension: string) {
  return `${title.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "-").trim() || "artifact"}.${extension}`;
}
export const extension = (name: string) => name.split(".").pop()?.toLowerCase() || "";
